import pointer from 'json-pointer';
import { mergeSchemas, updateStringValuesRecursively } from './util.js';
import { initLogger } from './logger.js';
import { DocumentLoadError } from './document-loader/document-loader.js';
/**
 * Stores a directory of schemas and resolves references against that directory.
 * Can merge objects recursively and will handle circular references.
 */
export class SchemaDirectory {
    /**
     * Initialise the SchemaDirectory. Does not load the schemas until loadSchemas is called.
     * @param documentLoader The document loader to use for loading documents.
     * @param debug Whether to log at debug level.
     */
    constructor(documentLoader, debug = false) {
        this.schemas = new Map();
        this.schemaTypes = new Map();
        this.PATTERN_CURRENTLY_VALIDATING = 'patternCurrentlyValidating';
        this.debug = debug;
        this.logger = initLogger(debug, 'schema-directory');
        this.documentLoader = documentLoader;
    }
    loadCurrentPatternAsSchema(pattern) {
        this.logger.debug('Loading current pattern as a schema.');
        this.schemas.set(this.PATTERN_CURRENTLY_VALIDATING, pattern);
        this.schemaTypes.set(this.PATTERN_CURRENTLY_VALIDATING, 'pattern');
    }
    /**
     * Initialise the SchemaDirectory. If the DocumentLoader implementation loads documents on startup, they will be loaded here.
     */
    async loadSchemas() {
        await this.documentLoader.initialise(this);
    }
    async lookupDefinition(schemaId, ref) {
        const schema = await this.getSchema(schemaId);
        if (!schema) {
            this.logger.warn(`Schema with $id ${schemaId} not found. Returning placeholder with warning message.`);
            return this.getMissingSchemaPlaceholder(ref || schemaId);
        }
        // If no ref (or empty ref), return the whole schema
        if (!ref || ref === '') {
            return schema;
        }
        return pointer.get(schema, ref);
    }
    async getDefinitionRecursive(definitionReference, currentSchemaId, visitedDefinitions) {
        const splitReference = definitionReference.split('#');
        let newSchemaId = splitReference[0];
        const ref = splitReference[1];
        visitedDefinitions.push(definitionReference);
        if (!newSchemaId) {
            newSchemaId = currentSchemaId;
            this.logger.debug(`Resolving reference ${ref} against current schema ${currentSchemaId}.`);
        }
        this.logger.debug(`Recursively resolving the reference, ref: ${ref}`);
        const definition = await this.lookupDefinition(newSchemaId, ref);
        if (!definition) {
            // Unreachable in practice: lookupDefinition returns a placeholder object
            // (getMissingSchemaPlaceholder) rather than a falsy value when a schema
            // cannot be resolved. Kept as a defensive guard.
            throw Error('schema missing!');
        }
        const definitionRef = definition['$ref'];
        if (!definitionRef) {
            this.logger.debug('Reached a definition with no ref, terminating recursive lookup.');
            return this.qualifyLocalReferences(definition, newSchemaId);
        }
        const newRef = definitionRef;
        if (visitedDefinitions.includes(newRef)) {
            this.logger.warn('Circular reference detected. Terminating reference lookup. Visited definitions: ' + visitedDefinitions);
            return definition;
        }
        const innerDef = await this.getDefinitionRecursive(newRef, newSchemaId, visitedDefinitions);
        const merged = mergeSchemas(innerDef, definition);
        const qualified = this.qualifyLocalReferences(merged, newSchemaId);
        return qualified;
    }
    getMissingSchemaPlaceholder(reference) {
        return {
            properties: {
                'missing-value': `MISSING OBJECT, ref: ${reference} could not be resolved`
            }
        };
    }
    /**
     *
     * @param definitionReference The reference to resolve. May be an absolute reference including a schema ID prefix, or a local reference.
     * @returns The resolved object, or an empty object if the object could not be resolved.
     */
    async getDefinition(definitionReference) {
        this.logger.debug(`Resolving ${definitionReference} from schema directory.`);
        const definition = await this.getDefinitionRecursive(definitionReference, this.PATTERN_CURRENTLY_VALIDATING, []);
        this.logger.debug(`Resolved definition ${JSON.stringify(definition, null, 2)}`);
        return definition;
    }
    /**
     * Once a definition has been resolved, we need to make sure the returned object has any leftover keys resolved against the schema they were fetched from.
     * The easiest way to do this is to qualify all local references (e.g #/defs/rate-limit-key) with their full schema ID.
     * That way when we instantiate them later, we have the ID of the schema they belong to.
     * @param definition  the definition object to look at references for
     * @param schemaId the schema ID to insert
     */
    qualifyLocalReferences(definition, schemaId) {
        return updateStringValuesRecursively(definition, (key, value) => {
            if (key === '$ref' && value.startsWith('#')) {
                const newReference = schemaId + value;
                this.logger.debug(`Detected a local reference: ${value}. Qualifying the reference with schema ID during resolution. `);
                this.logger.debug(`Qualified reference: ${newReference}`);
                return newReference;
            }
            return value;
        });
    }
    /**
     * Return the list of all loaded schemas.
     */
    getLoadedSchemas() {
        return [...this.schemas.keys()];
    }
    /**
     * Return the entire schema from the provided directory.
     * @param schemaId The ID of the schema to load.
     * @returns An entire schema as an object.
     */
    async getSchema(schemaId) {
        if (!this.schemas.has(schemaId)) {
            try {
                if (/^https?:\/\/json-schema\.org/.test(schemaId)) {
                    throw new Error(`Attempted to load standard JSON Schema with ID ${schemaId}. This is not supported.`);
                }
                const document = await this.documentLoader.loadMissingDocument(schemaId, 'schema');
                this.storeDocument(schemaId, 'schema', document);
                return document;
            }
            catch (err) {
                if (err instanceof DocumentLoadError) {
                    if (err.name === 'OPERATION_NOT_IMPLEMENTED') {
                        const registered = this.getLoadedSchemas();
                        this.logger.warn(`Schema with $id ${schemaId} not found. Returning undefined. Registered schemas: ${registered}`);
                        return undefined;
                    }
                }
                throw err;
            }
        }
        return this.schemas.get(schemaId);
    }
    async getPattern(patternId) {
        return await this.getSchema(patternId);
    }
    storeDocument(documentId, documentType, document) {
        this.logger.debug(`Storing document with ID ${documentId} of type ${documentType}.`);
        this.schemas.set(documentId, document);
        this.schemaTypes.set(documentId, documentType);
    }
    /**
     * Load a CALM document (e.g. an architecture) by reference via the DocumentLoader.
     * Unlike {@link getSchema}, this does not cache the result in the schema map and
     * accepts an explicit document type. Used to fetch raw sub-architecture documents
     * for recursive validation.
     */
    async loadDocument(documentId, type) {
        return this.documentLoader.loadMissingDocument(documentId, type);
    }
    /**
     * Return a new SchemaDirectory backed by the same DocumentLoader, pre-seeded with a
     * copy of this directory's already-loaded schemas.
     *
     * Isolation prevents AJV schema-ID collisions between a parent architecture and a
     * sub-architecture compilation, while reusing the warm cache means the base CALM
     * schemas are not re-fetched for every sub-architecture. The pattern-currently-
     * validating entry is intentionally not copied so the fork can load its own pattern.
     */
    fork() {
        const forked = new SchemaDirectory(this.documentLoader, this.debug);
        for (const [id, doc] of this.schemas) {
            if (id === this.PATTERN_CURRENTLY_VALIDATING) {
                continue;
            }
            forked.schemas.set(id, doc);
            const type = this.schemaTypes.get(id);
            if (type) {
                forked.schemaTypes.set(id, type);
            }
        }
        return forked;
    }
}
//# sourceMappingURL=schema-directory.js.map